(() => {
  'use strict';
  const fields = {game: 'Game', set: 'Set', rarity: 'Rarity', colour: 'Colour', type: 'Card type', condition: 'Condition', language: 'Language'};
  const normalise = value => String(value || '').toLocaleLowerCase().normalize('NFKD').replace(/[^a-z0-9]/g, '');
  const nameKey = title => title.replace(/^One Piece Card Game\s+[A-Z0-9-]+\s+/i, '').toLocaleLowerCase();
  const matches = (card, state, omit) => {
    if (state.q.trim().split(/\s+/).filter(Boolean).some(term => !card.search.includes(normalise(term)))) return false;
    if (state.available && !card.available) return false;
    if (state.min !== '' && card.price < Number(state.min) * 100) return false;
    if (state.max !== '' && card.price > Number(state.max) * 100) return false;
    return Object.keys(fields).every(key => key === omit || !state[key].length || state[key].includes(card[key]));
  };
  const initialState = () => ({q: '', available: false, min: '', max: '', sort: 'newest', page: 1, ...Object.fromEntries(Object.keys(fields).map(key => [key, []]))});
  const sortCards = (cards, sort) => cards.slice().sort((a, b) => {
    let order = 0;
    if (sort === 'price-asc') order = a.price - b.price;
    if (sort === 'price-desc') order = b.price - a.price;
    if (sort === 'name-asc') order = nameKey(a.title).localeCompare(nameKey(b.title), 'en', {numeric: true});
    if (sort === 'name-desc') order = nameKey(b.title).localeCompare(nameKey(a.title), 'en', {numeric: true});
    if (sort === 'newest') order = b.created - a.created;
    return order || b.id.localeCompare(a.id, 'en', {numeric: true});
  });

  function start(root) {
    if (root.dataset.initialised) return;
    root.dataset.initialised = 'true';
    const find = selector => root.querySelector(selector);
    const form = find('.pnp-filters');
    const query = find('#pnp-query');
    const sort = find('#pnp-sort');
    const dialog = find('.pnp-dialog');
    const panel = find('.pnp-desktop-panel');
    const grid = find('.pnp-grid');
    const count = find('.pnp-count');
    const status = find('.pnp-status');
    const pageSize = 24;
    let cards = [], state = initialState(), ready = false, typing;
    const options = {};

    function readURL() {
      const params = new URL(location.href).searchParams;
      state = initialState();
      state.q = params.get('pnp_q') || '';
      Object.keys(fields).forEach(key => state[key] = params.getAll(`pnp_${key}`).filter(value => options[key]?.includes(value)));
      state.available = params.get('pnp_available') === 'true' || params.get('filter.v.availability') === '1';
      for (const key of ['min', 'max']) {
        const value = params.get(`pnp_${key}`) ?? params.get(`filter.v.price.${key === 'min' ? 'gte' : 'lte'}`) ?? '';
        state[key] = value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0 ? value : '';
      }
      const nativeSort = {'price-ascending': 'price-asc', 'price-descending': 'price-desc', 'title-ascending': 'name-asc', 'title-descending': 'name-desc', 'created-descending': 'newest'};
      const value = params.get('pnp_sort') || nativeSort[params.get('sort_by')] || 'newest';
      state.sort = Array.from(sort.options).some(option => option.value === value) ? value : 'newest';
      state.page = Math.max(1, parseInt(params.get('pnp_page') || '1', 10) || 1);
    }
    function writeURL() {
      const url = new URL(location.href);
      [...url.searchParams.keys()].forEach(key => { if (key.startsWith('pnp_') || key.startsWith('filter.') || ['page', 'sort_by'].includes(key)) url.searchParams.delete(key); });
      if (state.q) url.searchParams.set('pnp_q', state.q);
      Object.keys(fields).forEach(key => state[key].forEach(value => url.searchParams.append(`pnp_${key}`, value)));
      if (state.available) url.searchParams.set('pnp_available', 'true');
      ['min', 'max'].forEach(key => {if (state[key] !== '') url.searchParams.set(`pnp_${key}`, state[key]);});
      if (state.sort !== 'newest') url.searchParams.set('pnp_sort', state.sort);
      if (state.page > 1) url.searchParams.set('pnp_page', state.page);
      if (url.href !== location.href) history.pushState(null, '', url);
    }
    function syncForm() {
      query.value = state.q;
      sort.value = state.sort;
      form.querySelectorAll('input').forEach(input => {
        if (input.name === 'available') input.checked = state.available;
        else if (input.type === 'checkbox') input.checked = state[input.name].includes(input.value);
        else input.value = state[input.name];
      });
    }
    function closeDrawer() { if (dialog.open) dialog.close(); }
    function button(text, onClick, label) {
      const element = document.createElement('button');
      element.type = 'button'; element.textContent = text;
      if (label) element.setAttribute('aria-label', label);
      element.addEventListener('click', onClick); return element;
    }
    function clear() { state = initialState(); syncForm(); render(); }
    function chips() {
      const target = find('.pnp-selected'); target.replaceChildren();
      const add = (label, action) => target.append(button(`${label} ×`, () => {action(); state.page = 1; syncForm(); render();}, `Remove ${label}`));
      if (state.q) add(`Search: ${state.q}`, () => state.q = '');
      Object.entries(fields).forEach(([key, label]) => state[key].forEach(value => add(`${label}: ${value}`, () => state[key] = state[key].filter(item => item !== value))));
      if (state.available) add('In stock', () => state.available = false);
      if (state.min !== '') add(`Min: ${state.min} ${root.dataset.currency}`, () => state.min = '');
      if (state.max !== '') add(`Max: ${state.max} ${root.dataset.currency}`, () => state.max = '');
      target.hidden = !target.children.length;
      if (target.children.length) target.append(button('Clear All', clear));
    }
    function materialise(card) {
      if (card.node) return card.node;
      const template = document.createElement('template'); template.innerHTML = card.html;
      const node = template.content.querySelector('.product-card');
      if (!node) throw new Error('Missing card markup');
      node.querySelectorAll('img[data-src]').forEach(img => {
        img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.removeAttribute('data-src'); img.removeAttribute('data-srcset');
        img.classList.remove('no-js-hidden');
      });
      node.querySelectorAll('.lazy-image').forEach(image => image.classList.remove('lazy-image'));
      card.node = node; return node;
    }
    function render(updateURL = true) {
      if (!ready) return;
      const result = sortCards(cards.filter(card => matches(card, state)), state.sort);
      const pages = Math.max(1, Math.ceil(result.length / pageSize));
      state.page = Math.min(state.page, pages);
      const offset = (state.page - 1) * pageSize;
      grid.replaceChildren(...result.slice(offset, offset + pageSize).map(materialise));
      count.textContent = `${result.length} of ${cards.length} cards${result.length ? ` · Showing ${offset + 1}–${Math.min(offset + pageSize, result.length)}` : ''}`;
      find('.pnp-empty').hidden = result.length !== 0;
      grid.hidden = result.length === 0;
      const pagination = find('.pnp-pagination'); pagination.replaceChildren();
      if (pages > 1) {
        const go = page => {state.page = page; render(); count.focus({preventScroll: true}); count.scrollIntoView({block: 'start', behavior: 'instant'});};
        const previous = button('Previous', () => go(state.page - 1)); previous.disabled = state.page === 1; pagination.append(previous);
        for (let page = 1; page <= pages; page++) {
          if (pages > 9 && page !== 1 && page !== pages && Math.abs(page - state.page) > 2) continue;
          const item = button(String(page), () => go(page), `Page ${page}`);
          if (page === state.page) item.setAttribute('aria-current', 'page');
          pagination.append(item);
        }
        const next = button('Next', () => go(state.page + 1)); next.disabled = state.page === pages; pagination.append(next);
      }
      Object.keys(fields).forEach(key => form.querySelectorAll(`input[name="${key}"]`).forEach(input => {
        const total = cards.filter(card => card[key] === input.value && matches(card, state, key)).length;
        input.nextElementSibling.textContent = `${input.value} (${total})`;
      }));
      find('.pnp-apply').textContent = `Show ${result.length} cards`;
      const selected = Object.keys(fields).reduce((n, key) => n + state[key].length, 0) + Number(state.available) + Number(state.min !== '') + Number(state.max !== '');
      find('.pnp-open').textContent = selected ? `Filters (${selected})` : 'Filters';
      chips();
      if (updateURL) writeURL();
    }
    function buildFacets() {
      const target = find('.pnp-facets'); target.replaceChildren();
      Object.entries(fields).forEach(([key, title]) => {
        options[key] = [...new Set(cards.map(card => card[key]).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'en', {numeric: true}));
        if (!options[key].length) return;
        const fieldset = document.createElement('fieldset');
        const legend = document.createElement('legend'); legend.textContent = title; fieldset.append(legend);
        options[key].forEach(value => {
          const label = document.createElement('label'); label.className = 'pnp-check';
          const input = document.createElement('input'); input.type = 'checkbox'; input.name = key; input.value = value;
          const text = document.createElement('span'); text.textContent = value;
          label.append(input, text); fieldset.append(label);
        });
        target.append(fieldset);
      });
    }
    async function load() {
      ready = false; status.hidden = false; find('.pnp-retry').hidden = true;
      status.textContent = 'Loading search for the full singles collection…';
      const collected = new Map(); let expected = null, pages = 1;
      try {
        for (let page = 1; page <= pages; page++) {
          const url = new URL(root.dataset.collectionUrl, location.origin);
          url.searchParams.set('view', 'pnp-card-index'); url.searchParams.set('page', page);
          const preview = new URL(location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          const controller = new AbortController(); const timer = setTimeout(() => controller.abort(), 20000);
          let data;
          try {
            const response = await fetch(url, {credentials: 'same-origin', signal: controller.signal});
            if (!response.ok) throw new Error(`Collection request ${response.status}`);
            data = await response.json();
          } finally { clearTimeout(timer); }
          if (data.version !== 1 || data.collection !== root.dataset.collection || data.page !== page || !Array.isArray(data.products)) throw new Error('Invalid collection response');
          if (expected === null) {expected = data.total; pages = data.pages;}
          if (!Number.isInteger(expected) || expected > 25000 || data.total !== expected || data.pages !== pages || pages < 1 || pages > 521) throw new Error('Collection changed or exceeds pagination limit');
          data.products.forEach(card => {
            if (collected.has(card.id)) throw new Error('Duplicate collection result');
            collected.set(card.id, card);
          });
          status.textContent = `Loading all singles… ${collected.size} of ${expected}`;
        }
        if (collected.size !== expected) throw new Error('Incomplete collection');
        cards = [...collected.values()].filter(card => card.single).map(card => ({...card, search: normalise(`${card.title} ${card.number}`)}));
        buildFacets(); readURL(); syncForm(); ready = true; render(false);
        find('.pnp-fallback').hidden = true;
        [find('.pnp-toolbar'), find('.pnp-help'), panel, find('.pnp-results')].forEach(element => { if (element) element.hidden = false; });
        find('#pnp-search-help').hidden = false;
        status.hidden = true;
        root.dataset.loadedCount = String(collected.size);
        root.dataset.cardCount = String(cards.length);
      } catch (error) {
        ready = false;
        status.textContent = 'Card search could not load the complete collection. Please retry, or browse all pages below.';
        find('.pnp-retry').hidden = false;
        console.error('Pixel & Pull card search:', error.message);
      }
    }
    query.addEventListener('input', () => {clearTimeout(typing); typing = setTimeout(() => {state.q = query.value; state.page = 1; render();}, 150);});
    sort.addEventListener('change', () => {state.sort = sort.value; state.page = 1; render();});
    form.addEventListener('change', event => {
      const input = event.target;
      if (!input.name) return;
      if (input.name === 'available') state.available = input.checked;
      else if (input.type === 'checkbox') state[input.name] = [...form.querySelectorAll(`input[name="${input.name}"]:checked`)].map(item => item.value);
      else state[input.name] = input.validity.valid ? input.value : '';
      state.page = 1; render();
    });
    form.addEventListener('input', event => {
      const input = event.target;
      if (!['min', 'max'].includes(input.name)) return;
      state[input.name] = input.validity.valid ? input.value : '';
      state.page = 1; render();
    });
    form.addEventListener('submit', event => {
      event.preventDefault();
      ['min', 'max'].forEach(key => {const input = form.elements.namedItem(key); state[key] = input.validity.valid ? input.value : '';});
      state.page = 1; render(); closeDrawer(); count.focus({preventScroll: true});
    });
    root.querySelectorAll('.pnp-clear').forEach(element => element.addEventListener('click', clear));
    find('.pnp-retry').addEventListener('click', load);
    find('.pnp-open').addEventListener('click', () => {dialog.append(form); dialog.showModal(); document.documentElement.style.overflow = 'hidden';});
    find('.pnp-close').addEventListener('click', closeDrawer);
    dialog.addEventListener('close', () => {panel.append(form); document.documentElement.style.overflow = ''; find('.pnp-open').focus();});
    dialog.addEventListener('click', event => {if (event.target === dialog) {const rect = dialog.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right) closeDrawer();}});
    const mobile = matchMedia('(max-width: 991px)'); mobile.addEventListener('change', () => {if (!mobile.matches) closeDrawer();});
    window.addEventListener('popstate', () => {if (ready) {readURL(); syncForm(); render(false);}});
    load();
  }
  document.querySelectorAll('[data-pnp-cards]').forEach(start);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-pnp-cards]').forEach(start));
})();
